import {Table} from 'antd';
import {get} from 'lodash-es';
import React, {
    FC,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {Link, useHistory} from 'react-router-dom';

import {ContentUnavailable} from 'components/content-unavailable';
import {Spinner} from 'components/spinner';
import {useTableFeatures} from 'components/table/hooks/use-table-features';
import {TableActions} from 'components/table/table-actions/table-actions';
import {loadMetadata} from 'entities/metadata';
import {TEntity, loadTableData, resetLoadedData} from 'modules/data';
import {initBlankFilter} from 'modules/data/actions/filter';
import {ReactComponent as ArrowRightIcon} from 'shared/assets/arrow-right.svg';
import {EEntityType} from 'shared/constants/entities';
import {useAppDispatch} from 'store/config/hooks';
import {openModal} from 'store/slices/modals-slice';
import {EModalType} from 'store/slices/modals-slice/modals-slice-types';

import {SettingMappingHeaderCsvField} from '../../constants/setting-program.constants';
import {useSettingProgramMapping} from '../../hooks/use-setting-program-mapping';

import './setting-program-mapping.less';

const {Column} = Table;

interface ISettingProgramMappingProps{
    entityName: string;
    url: string;
}
export const SettingProgramMapping: FC<ISettingProgramMappingProps> = (
    {entityName, url}: ISettingProgramMappingProps,
) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const entityUrl = `${url}/${entityName}`;
    const [isMetaLoading, setIsMetaLoading] = useState(false);
    const [isUnavailable, setIsUnavailable] = useState(false);

    const {
        wasFetchedWithContext,
        storedTableState,
        tableDataOptions,
        contextRawData,
        isMonitorParsingCsv,
        metadata,
        tableState,
        loading,
        filter,
        data,
        resetTableMode,
    } = useSettingProgramMapping({entityName});

    const {handleTableChange, rowSelection} = useTableFeatures({
        entityName,
        url: entityUrl,
        metadata,
        data,
    });

    useEffect(() => {
        history.replace(entityUrl);
    }, [entityName]);

    useEffect(() => {
        if (!metadata) {
            (async () => {
                setIsMetaLoading(true);
                await dispatch(loadMetadata(entityName, EEntityType.TABLE));
                await dispatch(initBlankFilter(entityName));
                setIsMetaLoading(false);
            })();
        }
    }, []);

    useEffect(() => {
        if (!isMetaLoading) {
            setIsUnavailable(!metadata);
        }
    }, [metadata, isMetaLoading]);

    useEffect(() => {
        const isFilterLoaded = filter && filter.loading === false;
        const shouldLoadData = !data || !data.rows;
        const shouldFilterData = !filter?.data && metadata?.isFilterable;
        const shouldLoadFilteredData = contextRawData && shouldLoadData && !shouldFilterData;

        if (shouldFilterData) dispatch(initBlankFilter(entityName));
        const paramsToBeConverted = {...filter?.data, ...storedTableState?.data};

        if (shouldLoadFilteredData && isFilterLoaded) {
            dispatch(loadTableData(entityName, {...tableDataOptions, paramsToBeConverted}, entityUrl));
        }
    }, [filter, entityName, data, contextRawData, storedTableState]);

    useEffect(() => {
        if (contextRawData && metadata?.useContext && !wasFetchedWithContext) {
            dispatch(loadMetadata(entityName, EEntityType.TABLE, true));
        }
    }, [metadata?.useContext, contextRawData, isMetaLoading]);

    useEffect(() => () => {
        dispatch(resetLoadedData(entityName, EEntityType.TABLE_STATE));
        dispatch(resetLoadedData(entityName, EEntityType.TABLE));
    }, [entityName]);

    const handleChange = (record: any) => {
        dispatch(openModal({
            modalName: EModalType.settingProgramMappingModal,
            entityName,
            modalParameters: {
                entityName,
                url: entityUrl,
                recordId: record?.id,
            },
        }));
    };

    const fields = useMemo(() => {
        if (isMonitorParsingCsv) {
            return [...metadata?.fields?.slice(0, -1), SettingMappingHeaderCsvField];
        }
        return metadata?.fields;
    }, [isMonitorParsingCsv, metadata?.fields]);

    const renderContent = () => {
        if (isMetaLoading) {
            return <Spinner />;
        }

        if (isUnavailable) {
            return <ContentUnavailable className="setting-program-mapping__content-unavailable" />;
        }

        return (
            <>
                <TableActions
                    url={entityUrl}
                    entityName={entityName}
                    metadata={metadata}
                />
                <Table
                    rowKey="id"
                    dataSource={data?.rows as Record<string, TEntity[]>[]}
                    showSorterTooltip={false}
                    onChange={handleTableChange}
                    rowSelection={{
                        type: 'checkbox',
                        ...rowSelection,
                    }}
                    loading={loading}
                    pagination={{
                        ...tableState.pagination,
                        position: ['bottomLeft'],
                        locale: {items_per_page: 'на странице', jump_to: ''},
                        showQuickJumper: {
                            goButton: <ArrowRightIcon className="pagination_jumper" />,
                        },
                        showSizeChanger: metadata?.isPageSizeChangerEnabled,
                        onChange: resetTableMode,
                    }}
                >
                    {fields?.map(field => (
                        <Column
                            key={field.key}
                            title={field.label}
                            dataIndex={field.key}
                            sorter
                            render={(_, record) => (
                                <Link
                                    to={entityUrl}
                                    onClick={() => handleChange(record)}
                                >
                                    {get(record, field.key)}
                                </Link>
                            )}
                        />
                    ))}
                </Table>
            </>
        );
    };

    return renderContent();
};
